import { z } from "zod";

import { STANDARD_POPULATION_BUDGET } from "~/shared/agent-core/completeness-policy";

import { GovernedInsightPresentationManifestSchema } from "./governed-insight-presentation";

// Catalog bound only; published columns stay capped at MAX_SELECTED_FIELDS. Sized for the widest
// catalog (two-dimension comparison sales with unit price) plus its reference id fields.
const MAX_FIELDS = 48;
const MAX_ROWS = STANDARD_POPULATION_BUDGET.artifactMaxRows;
/** Published present_report columns. Catalog fields may be wider; selected fieldKeys stay at this cap. */
export const MAX_SELECTED_FIELDS = 12;
const MAX_WARNINGS = 10;

export const isMonthBucketValue = (value: string): boolean => /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

export const isCalendarDayValue = (value: string): boolean => {
  if (!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(value)) {
    return false;
  }
  const [yearText, monthText, dayText] = value.split("-");
  if (!yearText || !monthText || !dayText) {
    return false;
  }
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return daysInMonth !== undefined && day <= daysInMonth;
};

const CALENDAR_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-06" -> "Sep 6, 2026"; anything else reads as empty. */
export const formatCalendarDay = (value: unknown): string => {
  if (typeof value !== "string" || !isCalendarDayValue(value)) {
    return "";
  }
  const [year, month, day] = value.split("-");
  const monthName = month ? CALENDAR_MONTHS[Number(month) - 1] : undefined;
  return year && monthName && day ? `${monthName} ${Number(day)}, ${year}` : "";
};

export const isDecimalStringValue = (value: string): boolean =>
  value.length <= 64 && value !== "-0" && /^-?(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/.test(value);

const boundedKey = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z][A-Za-z0-9_.-]*$/);
const boundedLabel = z.string().trim().min(1).max(96);
const calendarDay = z.string().refine(isCalendarDayValue, "Date must be a valid YYYY-MM-DD calendar day.");
const monthBucket = z.string().refine(isMonthBucketValue, "Month must be a valid YYYY-MM bucket.");
const reportTitle = z
  .string()
  .min(1)
  .max(72)
  .refine(value => value === value.trim(), "Title must not have leading or trailing whitespace.")
  .refine(value => !/[\u0000-\u001f\u007f]/.test(value), "Title must be a single line without control characters.");

// Entities the canvas can drill into. Adding a kind requires a matching route + label entry in the
// canvas entity-drill registry; unknown kinds render without a drill affordance.
export const EntityLinkKindSchema = z.enum([
  "note",
  "customer",
  "supplier",
  "product",
  "sales_order",
  "purchase_order",
  "invoice",
  "credit_note",
  "customer_payment",
  "deposit",
  "bill",
  "supplier_credit",
  "supplier_payment",
]);
export type EntityLinkKind = z.infer<typeof EntityLinkKindSchema>;

// Declares that a displayable field labels a linkable entity. The id (and, for mixed columns, the
// per-row kind) live in sibling role="reference" fields that are never published or rendered.
const EntityRefSchema = z
  .object({
    entity: EntityLinkKindSchema.optional(),
    routeIdKey: boundedKey.optional(),
    disclosure: z.enum(["model", "dataset_only"]).optional(),
    idKey: boundedKey,
    typeKey: boundedKey.optional(),
  })
  .strict()
  .refine(ref => ref.entity !== undefined || ref.typeKey !== undefined, "entityRef needs a static entity or a per-row typeKey.");

// Money cells carry exact fractional cents. Document totals land on whole cents; per-unit prices
// (a $0.4235/lb price) do not, and rounding those to cents would show the customer a price they
// never agreed to. moneyKind tells the canvas how many decimals to show. Display only: it never
// changes a stored or canonical value. Left undeclared, the canvas shows two decimals for values
// that are exact in cents and the extra digits only where they carry real precision.
export const MoneyKindSchema = z.enum(["total", "unitPrice"]);
export type MoneyKind = z.infer<typeof MoneyKindSchema>;

const ReportFieldKindSchema = z.enum([
  "text",
  "money",
  "percentage",
  "number",
  "calendarDay",
  "monthBucket",
  "boolean",
  "decimalString",
  "instant",
]);
export const REPORT_FIELD_KINDS = ReportFieldKindSchema.options;
export type ReportFieldKind = z.infer<typeof ReportFieldKindSchema>;
/** Kinds added after the original eight. An open browser tab that predates a kind reads it as text. */
export const ADDED_REPORT_FIELD_KINDS: readonly ReportFieldKind[] = ["instant"];

// An instant is one exact moment: an ISO date-time with an offset or Z that Date.parse reads.
// Consumers derive everything from that one epoch, so a value Luxon's own ISO parser would
// refuse (a long fraction) still reads as the same moment everywhere.
const instantSchema = z.string().datetime({ offset: true });
export const isInstantValue = (value: string): boolean => instantSchema.safeParse(value).success && Number.isFinite(Date.parse(value));

export const ReportFieldDefinitionSchema = z
  .object({
    key: boundedKey,
    label: boundedLabel,
    role: z.enum(["dimension", "measure", "attribute", "reference"]),
    requiredForIdentity: z.boolean(),
    semanticType: ReportFieldKindSchema,
    entityRef: EntityRefSchema.optional(),
    groupingIdKey: boundedKey.optional(),
    currencyCodeKey: boundedKey.optional(),
    moneyKind: MoneyKindSchema.optional(),
  })
  .strict()
  .refine(
    field => field.currencyCodeKey === undefined || field.semanticType === "money",
    "Only money fields may declare a row currency reference.",
  )
  .refine(field => field.moneyKind === undefined || field.semanticType === "money", "Only money fields may declare a money kind.");

export type ReportFieldDefinition = z.infer<typeof ReportFieldDefinitionSchema>;

const addPresentationSelectionIssues = (
  fieldKeys: string[],
  sort: { fieldKey: string } | undefined,
  ctx: z.RefinementCtx,
  fieldKeysPath: Array<string | number>,
  sortPath: Array<string | number>,
): void => {
  if (new Set(fieldKeys).size !== fieldKeys.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: fieldKeysPath, message: "Published fields must be unique." });
  }
  if (sort && !fieldKeys.includes(sort.fieldKey)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: sortPath, message: "Sort field must be published." });
  }
};

export const ReportPresentationSchema = z
  .object({
    title: reportTitle,
    mode: z.enum(["table", "record", "metrics"]).optional(),
    fieldKeys: z.array(boundedKey).min(1).max(MAX_SELECTED_FIELDS),
    sort: z
      .object({
        fieldKey: boundedKey,
        direction: z.enum(["asc", "desc"]),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((presentation, ctx) => {
    addPresentationSelectionIssues(presentation.fieldKeys, presentation.sort, ctx, ["fieldKeys"], ["sort", "fieldKey"]);
  });

export type ReportPresentation = z.infer<typeof ReportPresentationSchema>;

export const ReportMethodologySchema = z
  .object({
    reportKey: boundedKey,
    dateBasis: z.string().trim().min(1).max(80),
    timezone: z.string().trim().min(1).max(80),
    startDate: calendarDay.optional(),
    endDate: calendarDay.optional(),
    comparisonStartDate: calendarDay.optional(),
    comparisonEndDate: calendarDay.optional(),
    governedRanking: z
      .object({
        fieldKey: boundedKey,
        direction: z.enum(["asc", "desc"]),
        complete: z.boolean(),
      })
      .strict()
      .optional(),
    statuses: z.array(z.string().trim().min(1).max(48)).max(20),
    includedData: z.array(z.string().trim().min(1).max(120)).max(20),
    coverage: z.union([
      z
        .object({
          state: z.enum(["complete", "bounded", "partial"]).optional(),
          offset: z.number().int().min(0),
          returnedRows: z.number().int().min(0).max(MAX_ROWS),
          totalRows: z.number().int().min(0),
          truncated: z.boolean(),
          rowsCoverFullPopulation: z.boolean().optional(),
          populationCount: z.number().int().min(0).optional(),
          populationNoun: z.string().trim().min(1).max(48).optional(),
        })
        .strict(),
      z
        .object({
          state: z.enum(["complete", "bounded", "partial"]),
          returnedRows: z.number().int().min(0).max(MAX_ROWS),
          totalRows: z.number().int().min(0).optional(),
          boundedBy: z.string().trim().min(1).max(96).optional(),
          partialReason: z.string().trim().min(1).max(160).optional(),
          populationCount: z.number().int().min(0).optional(),
          populationNoun: z.string().trim().min(1).max(48).optional(),
        })
        .strict(),
    ]),
  })
  .strict()
  .superRefine((methodology, ctx) => {
    const hasComparisonStart = methodology.comparisonStartDate !== undefined;
    const hasComparisonEnd = methodology.comparisonEndDate !== undefined;
    if (hasComparisonStart !== hasComparisonEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasComparisonStart ? "comparisonEndDate" : "comparisonStartDate"],
        message: "Comparison start and end dates must be provided together.",
      });
    }
  });

export type ReportMethodology = z.infer<typeof ReportMethodologySchema>;

const DerivedPopulationWindowSchema = z
  .object({
    startDate: calendarDay,
    endDate: calendarDay,
    comparisonStartDate: calendarDay.optional(),
    comparisonEndDate: calendarDay.optional(),
  })
  .strict()
  .superRefine((window, ctx) => {
    if ((window.comparisonStartDate === undefined) !== (window.comparisonEndDate === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["comparisonEndDate"],
        message: "Comparison window dates must be provided together.",
      });
    }
  });

const DerivedPopulationLineageSchema = z
  .object({
    populationId: boundedKey,
    populationLabel: boundedLabel,
    inputRowCount: z.number().int().min(0).max(MAX_ROWS),
    outputRowCount: z.number().int().min(0).max(MAX_ROWS),
    grainKeys: z.array(boundedKey).min(1).max(4),
    window: DerivedPopulationWindowSchema.optional(),
    excludedRowCount: z.number().int().min(0).max(MAX_ROWS).optional(),
    contributorCount: z.number().int().min(0).max(MAX_ROWS).optional(),
    targetGrainKeys: z.array(boundedKey).min(1).max(4).optional(),
    measureKey: boundedKey.optional(),
  })
  .strict();

const lineageShape = { lineage: DerivedPopulationLineageSchema };

const DerivedAnalysisStepSchema = z.discriminatedUnion("operation", [
  z
    .object({
      ...lineageShape,
      operation: z.literal("filter_rows"),
      conditions: z
        .array(
          z
            .object({
              fieldKey: boundedKey,
              operator: z.enum([
                "equals",
                "not_equals",
                "lt",
                "lte",
                "gt",
                "gte",
                "in",
                "not_in",
                "contains",
                "is_blank",
                "is_set",
                "on_or_before",
                "on_or_after",
              ]),
              value: z.union([z.string().max(200), z.array(z.string().max(200)).min(1).max(20)]).optional(),
            })
            .strict(),
        )
        .min(1)
        .max(5),
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("count_by"),
      fieldKeys: z.array(boundedKey).min(1).max(2),
      countFieldKey: boundedKey,
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("calendar_run_rate"),
      measureKey: boundedKey,
      actualCents: z.string().regex(/^-?\d+$/),
      projectedCents: z.string().regex(/^-?\d+$/),
      asOfCalendarDay: calendarDay,
      elapsedDays: z.number().int().min(1).max(31),
      totalDays: z.number().int().min(28).max(31),
      basis: z.literal("calendar_day"),
      classification: z.literal("projection_not_forecast"),
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("aggregate_by"),
      dimensionKeys: z.array(boundedKey).min(1).max(4),
      measureKeys: z.array(boundedKey).min(1).max(8),
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("rank"),
      measureKey: boundedKey,
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("top_n"),
      measureKey: boundedKey,
      n: z.number().int().min(1).max(50),
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("share_of_total"),
      measureKey: boundedKey,
      shareFieldKey: boundedKey,
    })
    .strict(),
  z
    .object({
      ...lineageShape,
      operation: z.literal("running_total"),
      measureKey: boundedKey,
      valueFieldKey: boundedKey,
      shareFieldKey: boundedKey,
    })
    .strict(),
]);

export const DerivedAnalysisSchema = z
  .object({
    steps: z.array(DerivedAnalysisStepSchema).min(1).max(6),
    excludedRowCount: z.number().int().min(0).optional(),
  })
  .strict();

export type DerivedAnalysis = z.infer<typeof DerivedAnalysisSchema>;

export const ReportCellValueSchema = z.union([z.string().max(2_000), z.number().finite(), z.boolean(), z.null()]);
export type ReportCellValue = z.infer<typeof ReportCellValueSchema>;
export const ReportRowSchema = z.record(ReportCellValueSchema);
export type ReportRow = z.infer<typeof ReportRowSchema>;

const addFieldDefinitionIssues = (fields: ReportFieldDefinition[], ctx: z.RefinementCtx, path: Array<string | number>): boolean => {
  const fieldKeys = fields.map(field => field.key);
  if (!fields.some(field => field.requiredForIdentity)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: "A report requires at least one identity field." });
  }
  if (new Set(fieldKeys).size !== fieldKeys.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: "Field keys must be unique." });
    return false;
  }
  const fieldsByKey = new Map(fields.map(field => [field.key, field]));
  for (const fieldDefinition of fields) {
    if (fieldDefinition.role === "reference") {
      if (fieldDefinition.requiredForIdentity || fieldDefinition.semanticType !== "text" || fieldDefinition.entityRef) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: "Reference fields must be non-identity text fields without their own entityRef.",
        });
      }
      continue;
    }
    if (fieldDefinition.currencyCodeKey && fieldsByKey.get(fieldDefinition.currencyCodeKey)?.role !== "reference") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: "Money currencyCodeKey must name a declared reference field.",
      });
    }
    if (fieldDefinition.groupingIdKey && fieldsByKey.get(fieldDefinition.groupingIdKey)?.role !== "reference") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: "groupingIdKey must name a declared reference field.",
      });
    }
    if (!fieldDefinition.entityRef) {
      continue;
    }
    for (const refKey of [fieldDefinition.entityRef.idKey, fieldDefinition.entityRef.typeKey]) {
      if (refKey !== undefined && fieldsByKey.get(refKey)?.role !== "reference") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: "entityRef keys must name declared reference fields.",
        });
      }
    }
  }
  return true;
};

const cellMatchesKind = (kind: ReportFieldKind, value: unknown): boolean => {
  switch (kind) {
    case "text":
      return typeof value === "string";
    case "decimalString":
      return typeof value === "string" && isDecimalStringValue(value);
    case "calendarDay":
      return typeof value === "string" && calendarDay.safeParse(value).success;
    case "monthBucket":
      return typeof value === "string" && monthBucket.safeParse(value).success;
    case "instant":
      return typeof value === "string" && isInstantValue(value);
    case "boolean":
      return typeof value === "boolean";
    case "money":
    case "percentage":
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    default: {
      const unhandled: never = kind;
      return unhandled;
    }
  }
};

const addFieldCatalogIssues = (fields: ReportFieldDefinition[], rows: ReportRow[], ctx: z.RefinementCtx): void => {
  if (!addFieldDefinitionIssues(fields, ctx, ["fields"])) {
    return;
  }

  const fieldKeys = fields.map(field => field.key);
  const fieldsByKey = new Map(fields.map(field => [field.key, field]));
  rows.forEach((row, rowIndex) => {
    const rowKeys = Object.keys(row);
    for (const fieldKey of fieldKeys) {
      if (!Object.prototype.hasOwnProperty.call(row, fieldKey)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", rowIndex, fieldKey], message: "Row is missing a declared field." });
      }
    }
    for (const rowKey of rowKeys) {
      const field = fieldsByKey.get(rowKey);
      if (!field) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", rowIndex, rowKey], message: "Row contains an undeclared field." });
        continue;
      }
      const value = row[rowKey];
      if (field.currencyCodeKey && value !== null) {
        const currencyCode = row[field.currencyCodeKey];
        if (typeof currencyCode !== "string" || !/^[A-Z]{3}$/.test(currencyCode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rows", rowIndex, field.currencyCodeKey],
            message: "Money rows require a valid explicit ISO currency code.",
          });
        }
      }
      if (value === null) {
        continue;
      }
      if (!cellMatchesKind(field.semanticType, value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", rowIndex, rowKey],
          message: "Cell value does not match its semantic type.",
        });
      }
    }
  });
};

export const ReportDatasetSchema = z
  .object({
    artifactKind: z.literal("report-table"),
    fields: z.array(ReportFieldDefinitionSchema).min(1).max(MAX_FIELDS),
    rows: z.array(ReportRowSchema).max(MAX_ROWS),
    rowCount: z.number().int().min(0).max(MAX_ROWS),
    warnings: z.array(z.string().trim().min(1).max(240)).max(MAX_WARNINGS),
    unsafeDisplayFieldKeys: z.array(boundedKey).max(MAX_FIELDS).optional(),
    presentationManifest: GovernedInsightPresentationManifestSchema.optional(),
    freshness: z.object({ asOf: calendarDay.optional(), generatedAt: z.string().datetime().optional() }).strict().optional(),
    methodology: ReportMethodologySchema,
    derivedAnalysis: DerivedAnalysisSchema.optional(),
  })
  .strict()
  .superRefine((dataset, ctx) => {
    if (dataset.rowCount !== dataset.rows.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rowCount"], message: "Row count must match the supplied rows." });
    }
    if (dataset.methodology.coverage.returnedRows !== dataset.rowCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["methodology", "coverage", "returnedRows"],
        message: "Coverage must match the supplied rows.",
      });
    }
    const fieldKeys = new Set(dataset.fields.map(field => field.key));
    for (const fieldKey of dataset.unsafeDisplayFieldKeys ?? []) {
      if (!fieldKeys.has(fieldKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unsafeDisplayFieldKeys"],
          message: "Unsafe display fields must be declared in the field catalog.",
        });
      }
    }
    const runningTotal = dataset.derivedAnalysis?.steps.find(step => step.operation === "running_total");
    const interactionLocked = dataset.presentationManifest?.rowInteraction?.mode === "locked";
    if (runningTotal) {
      const ranking = dataset.methodology.governedRanking;
      if (!ranking || !ranking.complete || ranking.fieldKey !== runningTotal.measureKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["methodology", "governedRanking"],
          message: "Running totals require a complete governed ranking on the same measure.",
        });
      }
      if (!interactionLocked) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["presentationManifest", "rowInteraction"],
          message: "Running totals require locked row interaction.",
        });
      }
      for (const derivedFieldKey of [runningTotal.valueFieldKey, runningTotal.shareFieldKey]) {
        if (!fieldKeys.has(derivedFieldKey)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["derivedAnalysis", "steps"],
            message: "Running-total proof must reference declared derived fields.",
          });
        }
      }
    } else if (interactionLocked) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["presentationManifest", "rowInteraction"],
        message: "Row interaction may be locked only for a running-total analysis.",
      });
    }
    const analysisSteps = dataset.derivedAnalysis?.steps ?? [];
    for (const [stepIndex, step] of analysisSteps.entries()) {
      if ("measureKey" in step && !fieldKeys.has(step.measureKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["derivedAnalysis", "steps"],
          message: "Analysis measures must be declared in the field catalog.",
        });
      }
      if (step.operation === "share_of_total" && !fieldKeys.has(step.shareFieldKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["derivedAnalysis", "steps"],
          message: "Share proof must reference a declared derived field.",
        });
      }
      if (step.operation === "filter_rows") {
        for (const condition of step.conditions) {
          const laterAggregationDropsField = analysisSteps
            .slice(stepIndex + 1)
            .some(
              laterStep =>
                (laterStep.operation === "aggregate_by" && !laterStep.dimensionKeys.includes(condition.fieldKey)) ||
                (laterStep.operation === "count_by" && !laterStep.fieldKeys.includes(condition.fieldKey)),
            );
          if (!fieldKeys.has(condition.fieldKey) && !laterAggregationDropsField) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["derivedAnalysis", "steps"],
              message: "Filter proof fields must be declared in the field catalog.",
            });
          }
        }
      }
    }
    addFieldCatalogIssues(dataset.fields, dataset.rows, ctx);
  });

export type ReportDataset = z.infer<typeof ReportDatasetSchema>;

const validatePresentationAgainstFields = (
  fields: ReportFieldDefinition[],
  presentation: ReportPresentation,
  ctx: z.RefinementCtx,
): void => {
  const available = new Set(fields.map(field => field.key));
  for (const fieldKey of presentation.fieldKeys) {
    if (!available.has(fieldKey)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["presentation", "fieldKeys"], message: "Published field is unavailable." });
    }
  }
  for (const field of fields) {
    if (field.requiredForIdentity && !presentation.fieldKeys.includes(field.key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["presentation", "fieldKeys"],
        message: "Published fields must retain every identity field.",
      });
    }
  }
};

export const ActiveReportArtifactSchema = z
  .object({
    artifactKind: z.literal("report-table"),
    orgId: z.string().uuid(),
    artifactId: z.string().uuid(),
    version: z.number().int().positive(),
    dataset: ReportDatasetSchema,
    presentation: ReportPresentationSchema,
  })
  .strict()
  .superRefine((artifact, ctx) => validatePresentationAgainstFields(artifact.dataset.fields, artifact.presentation, ctx));

export type ActiveReportArtifact = z.infer<typeof ActiveReportArtifactSchema>;

export const ActiveReportDescriptorSchema = z
  .object({
    artifactKind: z.literal("report-table"),
    orgId: z.string().uuid(),
    artifactId: z.string().uuid(),
    version: z.number().int().positive(),
    title: reportTitle,
    availableFields: z.array(ReportFieldDefinitionSchema).min(1).max(MAX_FIELDS),
    publishedFieldKeys: z.array(boundedKey).min(1).max(MAX_SELECTED_FIELDS),
    unsafeDisplayFieldKeys: z.array(boundedKey).max(MAX_FIELDS).optional(),
    presentationManifest: GovernedInsightPresentationManifestSchema.optional(),
    sort: z
      .object({ fieldKey: boundedKey, direction: z.enum(["asc", "desc"]) })
      .strict()
      .optional(),
    methodology: ReportMethodologySchema,
  })
  .strict()
  .superRefine((descriptor, ctx) => {
    addFieldDefinitionIssues(descriptor.availableFields, ctx, ["availableFields"]);
    addPresentationSelectionIssues(descriptor.publishedFieldKeys, descriptor.sort, ctx, ["publishedFieldKeys"], ["sort", "fieldKey"]);
    validatePresentationAgainstFields(
      descriptor.availableFields,
      {
        title: descriptor.title,
        ...(descriptor.presentationManifest ? { mode: descriptor.presentationManifest.preferredMode } : {}),
        fieldKeys: descriptor.publishedFieldKeys,
        sort: descriptor.sort,
      },
      ctx,
    );
    const available = new Set(descriptor.availableFields.map(field => field.key));
    for (const fieldKey of descriptor.unsafeDisplayFieldKeys ?? []) {
      if (!available.has(fieldKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unsafeDisplayFieldKeys"],
          message: "Unsafe display fields must be available.",
        });
      }
    }
  });

export type ActiveReportDescriptor = z.infer<typeof ActiveReportDescriptorSchema>;

const ReportArtifactMutationCommitSchema = z
  .object({
    artifactKind: z.literal("report-table"),
    source: z.enum(["candidate", "current"]),
    orgId: z.string().uuid(),
    artifactId: z.string().uuid(),
    expectedVersion: z.number().int().min(0),
    presentation: ReportPresentationSchema,
    dataset: ReportDatasetSchema.optional(),
  })
  .strict()
  .superRefine((commit, ctx) => {
    if (commit.source === "candidate") {
      if (!commit.dataset) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dataset"], message: "Candidate commit requires a dataset." });
        return;
      }
      if (commit.expectedVersion !== 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedVersion"], message: "Candidate commit must begin at version zero." });
      }
      validatePresentationAgainstFields(commit.dataset.fields, commit.presentation, ctx);
      return;
    }
    if (commit.dataset) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dataset"], message: "Current revision must not send report rows." });
    }
    if (commit.expectedVersion < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedVersion"], message: "Current revision requires an active version." });
    }
  });

export const ReportArtifactCommitSchema = ReportArtifactMutationCommitSchema;

export type ReportArtifactCommit = z.infer<typeof ReportArtifactCommitSchema>;

const ORIGINAL_REPORT_FIELD_KINDS: ReadonlySet<ReportFieldKind> = new Set(
  REPORT_FIELD_KINDS.filter(kind => !ADDED_REPORT_FIELD_KINDS.includes(kind)),
);

/**
 * The one mapping for a reader that renders only some added kinds: every kind outside the
 * original eight and not named in `kinds` reads as text. `undefined` names none of them.
 */
export const fieldsForKinds = (fields: ReportFieldDefinition[], kinds: readonly string[] | undefined): ReportFieldDefinition[] => {
  const renders = (kind: ReportFieldKind) => ORIGINAL_REPORT_FIELD_KINDS.has(kind) || (kinds?.includes(kind) ?? false);
  if (fields.every(field => renders(field.semanticType))) {
    return fields;
  }
  return fields.map(field => (renders(field.semanticType) ? field : { ...field, semanticType: "text" }));
};

/** Applies fieldsForKinds to a commit's fields; row values are untouched. */
export const reportCommitForKinds = (commit: ReportArtifactCommit, kinds: readonly string[] | undefined): ReportArtifactCommit => {
  if (!commit.dataset) {
    return commit;
  }
  const fields = fieldsForKinds(commit.dataset.fields, kinds);
  return fields === commit.dataset.fields ? commit : { ...commit, dataset: { ...commit.dataset, fields } };
};
